/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { ActionGroup } from 'client-code/actions';
import { PreviewGroup, ScreenType } from 'client-code/previews';

import { APPLICATION_FEATURE_AREA } from '../../../config';
import { AppUtils } from '../../AppUtils';
import { LiveTvError } from '../../error';
import { VodInteractor } from '../../interactors';
import { PlaybackDataModelImpl } from '../../playback/impls';
import {
    TActionList,
    TActionsVodOfferParams,
    TPlayNextParams,
    TVodOfferPreviewCustomData,
} from '../../types';
import { MetadataType } from '../enums';
import { IMetadata, IMetadataModel, IVodPlaybackModel } from '../interfaces';

export class VodMetaDataModelImpl implements IMetadataModel {
    private static TAG: string = 'VodMetadataModelImpl';

    private mError: any = null;

    private mMetadata: IMetadata = {};

    private mServices = new VodInteractor();

    public getMetadata(): IMetadata {
        return this.mMetadata;
    }

    public getError(): LiveTvError {
        return this.mError;
    }

    private async retrieveActionDetails(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.mMetadata.watchVodAction = PlaybackDataModelImpl.getInstance().getActionDetails();
            if (AppUtils.isEmpty(this.mMetadata.watchVodAction)) {
                reject();
            } else {
                resolve();
            }
        });
    }

    private async fetchVodOfferPreviewData(
        vodOfferId: string,
        screenType: ScreenType,
        groups: PreviewGroup[],
        vodPreview?: TVodOfferPreviewCustomData,
    ): Promise<void> {
        return new Promise((resolve, reject) => {
            this.mServices
                .fetchVodFullPreview(vodOfferId, screenType, groups, vodPreview || {})
                .then((response: TVodOfferPreviewCustomData) => {
                    this.mMetadata.vodOfferPreview = response;
                    resolve();
                })
                .catch((error) => {
                    this.mError = error;
                    reject(error);
                });
        });
    }

    private async retrieveVodActions(): Promise<void> {
        const { offerId } = this.mMetadata.watchVodAction?.kernel || {};
        const requestParams: TActionsVodOfferParams = {
            vodOfferId: offerId || '',
            groups: [ActionGroup.ScheduleOnepass, ActionGroup.ScheduleBookmark],
            screenType: APPLICATION_FEATURE_AREA.PLAYER,
        };
        return new Promise((resolve, reject) => {
            this.mServices
                .getVodOfferActionsV2(requestParams)
                .then((actionList: TActionList) => {
                    this.mMetadata.actionList = actionList.actionList;
                    if (AppUtils.isEmpty(this.mMetadata.actionList)) {
                        reject();
                    } else {
                        resolve();
                    }
                })
                .catch((error) => {
                    this.mError = error;
                    reject(error);
                });
        });
    }

    private async retrieveVodFullPreview(): Promise<void> {
        const { offerId } = this.mMetadata.watchVodAction?.kernel || {};
        const { vodOfferPreview } = this.mMetadata;
        return this.fetchVodOfferPreviewData(
            offerId || '',
            ScreenType.FullBanner,
            [PreviewGroup.Casts, PreviewGroup.Description],
            vodOfferPreview,
        );
    }

    private async retrieveVodPreview(): Promise<void> {
        const { offerId } = this.mMetadata.watchVodAction?.kernel || {};
        return this.fetchVodOfferPreviewData(
            offerId || '',
            ScreenType.StandardBanner,
            [
                PreviewGroup.Metadata,
                PreviewGroup.Linear,
                PreviewGroup.OnDemand,
                PreviewGroup.VodStatus,
            ],
            {},
        );
    }

    private async fetchPlayNextForVod(collapsedVodItemId: string): Promise<void> {
        try {
            const { contentId, partnerId, collectionId, contentSupplierPartnerId } =
                this.mMetadata.watchVodAction?.kernel || {};
            const playNextParams: TPlayNextParams = {
                contentId: contentId || '',
                unifiedCollectionId: collectionId || '',
                filter: 'vodOnDemand',
                sort: 'seasonOrYearAsc',
            };
            if (partnerId) playNextParams.partnerId = partnerId;
            if (contentSupplierPartnerId)
                playNextParams.contentSupplierPartnerId = contentSupplierPartnerId;
            if (collapsedVodItemId) playNextParams.collapsedVodItemId = collapsedVodItemId;
            this.mMetadata.playNextEpisodes = await this.mServices.fetchPlayNext(playNextParams);
            return Promise.resolve();
        } catch (error) {
            this.mError = error;
            return Promise.reject(error);
        }
    }

    private async fetchPlayNextPreviewForVod(collapsedVodItemId: string): Promise<void> {
        try {
            const { contentId, partnerId, contentSupplierPartnerId } =
                this.mMetadata.watchVodAction?.kernel || {};
            this.mMetadata.playNextPreview = await this.mServices.fetchContentPreviewV3({
                contentId: contentId || '',
                partnerId,
                collapsedVodItemId,
                groups: [PreviewGroup.Metadata, PreviewGroup.Linear],
                contentSupplierPartnerId,
            });
            return Promise.resolve();
        } catch (error) {
            this.mError = error;
            return Promise.reject(error);
        }
    }

    private async fetchPlayNextActionsForVod(collapsedVodItemId: string): Promise<void> {
        try {
            const { contentId, contentSupplierPartnerId, partnerId } =
                this.mMetadata.watchVodAction?.kernel || {};
            this.mMetadata.playNextActions = await this.mServices.getPlayNextContentActions({
                contentId: contentId || '',
                screenType: APPLICATION_FEATURE_AREA.PLAYER,
                contentSupplierPartnerId,
                collapsedVodItemId,
                partnerId,
            });
            return Promise.resolve();
        } catch (error) {
            this.mError = error;
            return Promise.reject(error);
        }
    }

    public prepareMetadataByType(playbackType: {
        metadataType:
            | MetadataType.ACTION_DETAILS
            | MetadataType.FULL_INFO_PREVIEW
            | MetadataType.PREVIEW
            | MetadataType.VOD_ACTION_LIST
            | MetadataType.PLAY_NEXT_UNIFIED_COLLECTION
            | MetadataType.PLAY_NEXT_PREVIEW
            | MetadataType.PLAY_NEXT_ACTIONS;
        vodPlaybackModel: IVodPlaybackModel;
    }): Promise<void> {
        const { metadataType, vodPlaybackModel } = playbackType;
        const collapsedVodItemId = vodPlaybackModel?.collapsedVodItemId || '';
        switch (metadataType) {
            case MetadataType.ACTION_DETAILS:
                return this.retrieveActionDetails();
            case MetadataType.FULL_INFO_PREVIEW:
                return this.retrieveVodFullPreview();
            case MetadataType.PREVIEW:
                return this.retrieveVodPreview();
            case MetadataType.VOD_ACTION_LIST:
                return this.retrieveVodActions();
            case MetadataType.PLAY_NEXT_UNIFIED_COLLECTION:
                return this.fetchPlayNextForVod(collapsedVodItemId);
            case MetadataType.PLAY_NEXT_PREVIEW:
                return this.fetchPlayNextPreviewForVod(collapsedVodItemId);
            case MetadataType.PLAY_NEXT_ACTIONS:
                return this.fetchPlayNextActionsForVod(collapsedVodItemId);

            default:
                return Promise.resolve();
        }
    }
}
